// apps/frontend/src/utils/addressAutocomplete.js
// Debounced address autocomplete for a single .newc-form-address wrapper.
// search(query) is injected so the same logic serves the editor preview.
// (The production widget reimplements the same behavior inline — keep in sync.)
const MIN_CHARS = 5;

let uidCounter = 0;
function nextUid(prefix) {
  uidCounter += 1;
  return prefix + '-' + uidCounter;
}

const DEFAULT_LABELS = {
  searching: 'Ricerca in corso…',
  empty: 'Nessun indirizzo trovato',
  error: 'Errore nella ricerca',
  civicoLabel: 'Numero civico',
  civicoPlaceholder: 'Es. 12, 12/A, s.n.c.',
  civicoRequired: 'Inserisci il numero civico',
};

export function wireAddressField(wrapper, { search, debounceMs = 900, labels } = {}) {
  if (!wrapper || wrapper.dataset.wired === '1') return;
  wrapper.dataset.wired = '1';
  const input = wrapper.querySelector('.newc-form-address-input');
  const hidden = wrapper.querySelector('.newc-form-field[data-type="address"], .newc-array-field[data-type="address"]');
  const results = wrapper.querySelector('.newc-form-address-results');
  if (!input || !hidden || !results) return;

  const L = Object.assign({}, DEFAULT_LABELS, labels || {});
  const lens = wrapper.querySelector('.newc-form-address-lens');
  const spinner = wrapper.querySelector('.newc-form-address-spinner');

  const civicoRow = wrapper.querySelector('.newc-form-address-civico');
  const civicoInput = wrapper.querySelector('.newc-form-address-civico-input');
  const civicoLabelEl = wrapper.querySelector('.newc-form-address-civico-label');
  const civicoError = wrapper.querySelector('.newc-form-address-civico-error');
  if (civicoLabelEl) civicoLabelEl.textContent = L.civicoLabel;
  if (civicoInput) {
    civicoInput.placeholder = L.civicoPlaceholder;
    civicoInput.setAttribute('aria-label', L.civicoLabel);   // the label span is not a <label>; name the input explicitly
  }
  if (civicoError) civicoError.textContent = L.civicoRequired;

  const showCivico = () => {
    if (!civicoRow) return;
    civicoRow.style.display = 'flex';
    if (civicoInput) { try { civicoInput.focus(); } catch { /* jsdom */ } }
  };
  const hideCivico = () => {
    if (civicoRow) { civicoRow.style.display = 'none'; civicoRow.classList.remove('newc-form-invalid'); }
    if (civicoInput) civicoInput.value = '';
    if (civicoError) civicoError.style.display = 'none';
  };

  let timer = null;
  let items = [];
  let activeIndex = -1;
  let inFlight = false;
  let pendingQuery = null;   // last query requested while another was in flight
  let selectedLabel = null;  // label of the last picked suggestion
  let shownQuery = null;     // query whose results are currently displayed

  const listboxId = results.id || (results.id = nextUid('newc-addr-listbox'));

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', listboxId);
  results.setAttribute('role', 'listbox');

  const setExpanded = (open) => { input.setAttribute('aria-expanded', open ? 'true' : 'false'); };

  const setIcon = (state) => {
    if (!lens || !spinner) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const spin = state === 'loading' && !reduce;
    lens.style.display = spin ? 'none' : 'block';
    spinner.style.display = spin ? 'block' : 'none';
  };

  const setActiveDescendant = () => {
    const active = activeIndex >= 0 ? items[activeIndex] : null;
    if (active && active.el.id) {
      input.setAttribute('aria-activedescendant', active.el.id);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const clearActiveStyles = () => {
    items.forEach((it) => {
      it.el.setAttribute('aria-selected', 'false');
      it.el.style.background = '';
    });
  };

  const setActive = (index) => {
    if (!items.length) { activeIndex = -1; setActiveDescendant(); return; }
    if (index < 0) index = 0;
    if (index > items.length - 1) index = items.length - 1;
    activeIndex = index;
    clearActiveStyles();
    const active = items[activeIndex];
    active.el.setAttribute('aria-selected', 'true');
    active.el.style.background = '#eff6ff';
    setActiveDescendant();
  };

  const hide = () => {
    results.style.display = 'none';
    results.innerHTML = '';
    results.removeAttribute('aria-busy');
    items = [];
    activeIndex = -1;
    shownQuery = null;
    setActiveDescendant();
    setExpanded(false);
  };

  const selectItem = (addr) => {
    if (timer) clearTimeout(timer);
    input.value = addr.label || '';
    hidden.value = JSON.stringify(addr);
    selectedLabel = addr.label || '';
    hide();
    if (!String(addr.number || '').trim()) showCivico();
    else hideCivico();
  };

  const showStatusRow = (text) => {
    results.innerHTML = '';
    results.removeAttribute('aria-busy');
    items = [];
    activeIndex = -1;
    setActiveDescendant();
    const row = document.createElement('div');
    row.setAttribute('role', 'status');
    row.setAttribute('aria-live', 'polite');
    row.textContent = text;
    row.style.cssText = 'padding:11px 12px;font-size:13px;color:#6b7280;';
    results.appendChild(row);
    results.style.display = 'block';
    setExpanded(true);
  };

  const showLoading = () => {
    results.innerHTML = '';
    items = [];
    activeIndex = -1;
    setActiveDescendant();
    results.setAttribute('aria-busy', 'true');
    const row = document.createElement('div');
    row.setAttribute('role', 'status');
    row.setAttribute('aria-live', 'polite');
    row.textContent = L.searching;
    row.style.cssText = 'padding:11px 12px;font-size:13px;color:#6b7280;';
    results.appendChild(row);
    results.style.display = 'block';
    setExpanded(true);
  };

  const render = (found) => {
    results.innerHTML = '';
    results.removeAttribute('aria-busy');
    items = [];
    activeIndex = -1;

    if (!found.length) {
      showStatusRow(L.empty);
      return;
    }

    found.forEach((addr) => {
      const opt = document.createElement('div');
      opt.className = 'newc-form-address-option';
      opt.id = nextUid('newc-addr-option');
      opt.setAttribute('role', 'option');
      opt.setAttribute('aria-selected', 'false');
      opt.textContent = addr.label || '';
      opt.style.cssText = 'padding:11px 12px;cursor:pointer;font-size:13px;color:#1f2937;border-bottom:1px solid #f3f4f6;min-height:44px;box-sizing:border-box;display:flex;align-items:center;';
      opt.addEventListener('mouseover', () => {
        const idx = items.findIndex((it) => it.el === opt);
        if (idx !== -1) setActive(idx);
      });
      // mousedown (not click): fires before the input blur that would hide the list.
      opt.addEventListener('mousedown', (e) => {
        e.preventDefault();
        selectItem(addr);
      });
      results.appendChild(opt);
      items.push({ el: opt, addr });
    });
    results.style.display = 'block';
    setExpanded(true);
    setActiveDescendant();
  };

  const isAlreadyShown = (q) => q === shownQuery && results.style.display === 'block';

  const finishRun = () => {
    inFlight = false;
    if (pendingQuery !== null && pendingQuery.length >= MIN_CHARS && !isAlreadyShown(pendingQuery)) {
      const next = pendingQuery;
      pendingQuery = null;
      run(next);
    } else {
      pendingQuery = null;
      setIcon('idle');
    }
  };

  const run = (q) => {
    if (inFlight) { pendingQuery = q; return; }   // single in-flight: remember the latest
    inFlight = true;
    setIcon('loading');
    showLoading();
    Promise.resolve()
      .then(() => search(q))
      .then(
        (found) => {
          try { shownQuery = q; render(found); }
          catch { shownQuery = null; showStatusRow(L.error); }
          finally { finishRun(); }
        },
        () => { shownQuery = null; showStatusRow(L.error); finishRun(); }
      );
  };

  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (selectedLabel !== null && q === selectedLabel) return;   // skip re-search of a fresh selection
    selectedLabel = null;
    hidden.value = '';                                           // any edit invalidates a prior selection
    hideCivico();
    if (timer) clearTimeout(timer);
    if (q.length < MIN_CHARS) { pendingQuery = null; setIcon('idle'); hide(); return; }
    if (isAlreadyShown(q)) { setIcon('idle'); return; }          // dedup consecutive identical query
    setIcon('loading');
    timer = setTimeout(() => run(q), debounceMs);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      if (!items.length) return;
      e.preventDefault();
      setActive(activeIndex < 0 ? 0 : activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      setActive(activeIndex < 0 ? items.length - 1 : activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        selectItem(items[activeIndex].addr);
      }
    } else if (e.key === 'Escape') {
      if (results.style.display === 'block') {
        e.preventDefault();
        hide();
      }
    }
  });

  input.addEventListener('blur', () => { setTimeout(hide, 150); });

  if (civicoInput) {
    civicoInput.addEventListener('input', () => {
      if (!hidden.value) return;
      let obj;
      try { obj = JSON.parse(hidden.value); } catch { return; }
      obj.number = civicoInput.value.trim();
      hidden.value = JSON.stringify(obj);
      if (civicoError) civicoError.style.display = 'none';
      if (civicoRow) civicoRow.classList.remove('newc-form-invalid');
    });
  }
}
